import React, { useEffect, useState } from "react";
import api from "@/api";

/**
 * SecuredImage fetches an image from a protected API endpoint using the Bearer token.
 * Props:
 *   src: the API endpoint or image path (relative to /images/)
 *   alt: alt text
 *   className: CSS classes for the image
 *   style: style object
 *   fallback: React node to show if image fails or is loading
 *   ...rest: other img props
 */
export default function SecuredImage({
  src,
  alt,
  className = "",
  style = {},
  fallback = null,
  ...rest
}) {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // VITE debug flag
  const isDebug =
    (typeof window !== "undefined" && window.DEBUG_MODE) ||
    (import.meta.env && import.meta.env.VITE_DEBUG_MODE === "true");
  if (isDebug) console.log("[SecuredImage] isDebug:", isDebug);
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(false);
    setImgUrl(null);
    if (!src) {
      if (isDebug) console.warn("[SecuredImage] No src provided");
      setLoading(false);
      setError(true);
      return;
    }
    // A path relative to the api instance's baseURL, unless src is a full URL (prefixing
    // config.apiBaseUrl as well gave /v1/v1/images/... where that base URL is relative)
    let apiUrl = src;
    if (!src.startsWith("http")) {
      apiUrl = `/images/${src}`;
    }

    // Always fetch image, no cache

    const token = localStorage.getItem("token");
    if (isDebug) {
      console.log(
        "[SecuredImage] Fetching image:",
        apiUrl,
        "with token:",
        token ? "[present]" : "[none]"
      );
    }
    api
      .get(apiUrl, {
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      })
      .then((response) => {
        if (!isMounted) return;
        const url = URL.createObjectURL(response.data);
        setImgUrl(url);
        setLoading(false);
        if (isDebug) {
          console.log("[SecuredImage] Image loaded:", apiUrl);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(true);
        setLoading(false);
        if (isDebug)
          console.error("[SecuredImage] Failed to load image:", apiUrl, err);
      });
    return () => {
      isMounted = false;
      // Do not revoke cached URLs here, only revoke if not cached
    };
    // eslint-disable-next-line
  }, [src]);

  if (loading || error || !imgUrl) {
    return fallback || null;
  }
  return (
    <img src={imgUrl} alt={alt} className={className} style={style} {...rest} />
  );
}
